import * as React from 'react';
import { TextAvatar } from './TextAvatar';
import { Flex } from '../compositeComponents/FlexHelpers';
import { Span14, Span12 } from '../typography/Body';
import { PublicAmorgUser, userHelper } from '../../amorg-common';
import { colors } from '../colors/color';
import { colorGenerator } from '../colors/colorGenerator';
import { amorgStyles } from '../styles/amorgStyles';
import styled from 'styled-components';
import { IconAvatar } from './IconAvatar';
import { txt } from '../../txt';

const DEFAULT_HEIGHT = '50px';

const ellipsisStyle: React.CSSProperties = {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

export type BaseAvatarCardProps = {
    avatar:React.ReactNode,
    primaryText:string,
    secondaryText:string,
    noBorder?: boolean,
    minHeight?:string,
    minWidth?: string,
    onClick?: () => void,
};

function BaseAvatarCard (props: BaseAvatarCardProps): JSX.Element
{
    return (
        <BaseCard hover={props.onClick !== undefined} minHeight={props.minHeight} minWidth={props.minWidth} border={!props.noBorder}
            onClick={props.onClick}
            role={props.onClick ? 'button' : undefined}
            tabIndex={props.onClick ? 0 : undefined}> 
            
            <Flex gap={8} alignItems='center'>
                {props.avatar}
                <Flex flexDirection="column" grow='1'>
                    <Span14 style={ellipsisStyle}>
                        {props.primaryText}
                    </Span14>
                    <Span12 color='active' style={ellipsisStyle}>
                        {props.secondaryText}
                    </Span12>
                </Flex>
            </Flex>
        </BaseCard>
    );
}

export type AvatarCardProps = {
    avatarText:string,
    avatarColor?: string,
    primaryText:string,
    secondaryText:string,
    noBorder?: boolean,
    minHeight?:string,
    minWidth?: string,
    onClick?: () => void,
};

export function AvatarCard (props: AvatarCardProps): JSX.Element
{
    return (
        <BaseAvatarCard
            avatar={
                <TextAvatar size={'32px'} backgroundColor={props.avatarColor || colors.avatar.background}
                    text={props.avatarText}
                />
            }
            primaryText={props.primaryText}
            secondaryText={props.secondaryText || ''}
            noBorder={props.noBorder}
            minHeight={props.minHeight}
            minWidth={props.minWidth}
            onClick={props.onClick}
        />
    );
}

export function UserAvatarCard (props:{
    user?:{ initials:string, displayName: string, email?: string, role?:string, permissionGroup?: string },
    secondaryTextType?: 'email' | 'role' | 'permission-group' | 'custom',
    secondaryText?: string,
    noUserText?: string,
    minWidth?: string,
    noBorder?: boolean,
    onClick?: () => void,
}): JSX.Element
{

    if(props.user === undefined) {
        return <BaseAvatarCard
            avatar={
                <IconAvatar
                    backgroundColor={colors.avatar.backgroundMuted}
                    contentColor='#3d3737'
                    size="32px"
                    icon='interface_user_user'
                    onClick={props.onClick}
                />
            }
            primaryText={props.noUserText || "Ingen valgt"}
            secondaryText=""
            noBorder={props.noBorder}
            minWidth={props.minWidth}
            onClick={props.onClick}
        />;
    }

    let secondaryText = props.user?.permissionGroup || '';
    if(props.secondaryTextType === 'email') {
        secondaryText = props.user?.email || 'ingen email';
    } else if(props.secondaryTextType === 'role') {
        secondaryText = props.user?.role || 'ingen rolle';
    } else if(props.secondaryTextType === 'custom') {
        secondaryText = props.secondaryText || '';
    }

    return (
        <AvatarCard
            avatarText={props.user.initials}
            avatarColor={colorGenerator.getDarkColorFromString(props.user.displayName)}
            primaryText={props.user.displayName}
            secondaryText={secondaryText}
            noBorder={props.noBorder}
            minWidth={props.minWidth}
            onClick={props.onClick}
        />
    );
}

export function FullUserAvatarCard (props:{
    user?:PublicAmorgUser,
    secondaryTextType?: 'email' | 'role' | 'custom',
    secondaryText?: string,
    noUserText?: string,
    noBorder?: boolean,
    minWidth?: string,
    onClick?: () => void,
}): JSX.Element
{
    return <UserAvatarCard 
        user={props.user 
            ?   { 
                    initials: props.user.initials, 
                    displayName: userHelper.getPublicUserFullName(props.user), 
                    email: props.user.email, 
                    role: props.user.currentRole?.title,
                    permissionGroup: txt.getDynamic('PermissionGroupId_' + props.user.permissionGroup.id)
                } 
            : undefined
        }
        secondaryTextType={props.secondaryTextType}
        secondaryText={props.secondaryText}
        noUserText={props.noUserText}
        noBorder={props.noBorder}
        minWidth={props.minWidth}
        onClick={props.onClick}
    />;
}

const BaseCard = styled.div<{hover:boolean, minHeight?:string, minWidth?:string, border?: boolean}>`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 4px 8px;
    background: ${colors.card.outlined.default.background};
    border: ${props => props.border === false ? '1px solid transparent' : `1px solid ${colors.card.outlined.default.border}`};
    border-radius: ${amorgStyles(colors).card.borderRadius};

    ${props => props.hover && `
        cursor: pointer;
        &:hover {
            border: 1px solid ${colors.inputState.default.borderHover};
        }
    `}
    min-height: ${props => props.minHeight || DEFAULT_HEIGHT};
    min-width: ${props => props.minWidth};
`;